// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Image, Pressable } from 'react-native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import { MysteryPrizeIcon } from '@/features/hole/assets';
import { styles } from '@/features/hole/screen/styles';

type MatchStatusBanner = {
  key: string;
  icon: string;
  text: string;
  color?: string;
};

type HoleHeaderCardProps = {
  holeNumber: number;
  showLeaderboardLabel: boolean;
  skinsCarryText?: string;
  matchStatusBanners?: MatchStatusBanner[];
  hasMysteryPrizeForHole: boolean;
  mysteryPrizeText: string;
  onOpenMystery: () => void;
  showMysteryPrizeButton: boolean;
  onOpenMysteryPrizeSetup: () => void;
  holeName?: string;
  holeDescription?: string;
  showHoleDetails: boolean;
  onToggleDetails: () => void;
  /** "3 slag · senaste 182 m" from automatic shot tracking; hidden when absent. */
  shotTrackerText?: string;
};

export function HoleHeaderCard({
  holeNumber,
  showLeaderboardLabel,
  skinsCarryText,
  matchStatusBanners,
  hasMysteryPrizeForHole,
  mysteryPrizeText,
  onOpenMystery,
  showMysteryPrizeButton,
  onOpenMysteryPrizeSetup,
  holeName,
  holeDescription,
  showHoleDetails,
  onToggleDetails,
  shotTrackerText,
}: HoleHeaderCardProps) {
  return (
    <View style={styles.holeInfoBlock}>
      {showLeaderboardLabel ? (
        <View style={styles.titleRow}>
          <View style={styles.holeInfoRow}>
            <Text style={styles.holeInfoBadge}>{t('hole.leaderboardShortTitle')}</Text>
          </View>
        </View>
      ) : null}
      {skinsCarryText ? (
        <View style={styles.skinsCarryRow} testID={`skins-carry-hole-${holeNumber}`}>
          <Text style={styles.skinsCarryText}>{skinsCarryText}</Text>
        </View>
      ) : null}
      {shotTrackerText ? (
        <View style={styles.shotTrackerRow} testID={`shot-tracker-hole-${holeNumber}`}>
          <Text style={styles.shotTrackerText}>{shotTrackerText}</Text>
        </View>
      ) : null}
      {(matchStatusBanners ?? []).map((banner) => (
        <View key={banner.key} style={styles.matchStatusBanner}>
          <Text style={styles.matchStatusIcon}>{banner.icon}</Text>
          <Text
            style={[styles.matchStatusText, banner.color ? { color: banner.color } : null]}
            numberOfLines={2}
          >
            {banner.text}
          </Text>
        </View>
      ))}
      {hasMysteryPrizeForHole ? (
        <Pressable style={styles.mysteryPrizeRow} onPress={onOpenMystery}>
          <Image source={MysteryPrizeIcon} style={styles.mysteryPrizeIcon} resizeMode="contain" />
          <Text style={styles.mysteryPrizeText}>{mysteryPrizeText}</Text>
        </Pressable>
      ) : null}
      {showMysteryPrizeButton ? (
        <Pressable
          style={styles.mysteryPrizeSetupButton}
          onPress={onOpenMysteryPrizeSetup}
          accessibilityRole="button"
          accessibilityLabel={t('hole.mysteryPrizeSetup')}
        >
          <Image source={MysteryPrizeIcon} style={styles.mysteryPrizeIcon} resizeMode="contain" />
          <Text style={styles.mysteryPrizeSetupButtonText}>{t('hole.mysteryPrizeSetup')}</Text>
        </Pressable>
      ) : null}
      {holeName ? <Text style={styles.holeInfoName}>{holeName}</Text> : null}
      {holeDescription ? (
        <>
          {showHoleDetails ? <Text style={styles.holeInfoDescription}>{holeDescription}</Text> : null}
          <Pressable style={styles.detailsToggle} onPress={onToggleDetails}>
            <Text style={styles.detailsToggleText}>
              {showHoleDetails ? t('hole.hideDetails') : t('hole.showDetails')}
            </Text>
          </Pressable>
        </>
      ) : null}
    </View>
  );
}
